import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  ExternalLink,
  Mail,
  MapPin,
  Phone,
  Search,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DirectoryNavigation } from "@/components/directory-navigation";
import { LocalClearanceSection } from "@/components/local-clearance-section";
import { LocalCleaningSection } from "@/components/local-cleaning-section";
import { LocalDismantlingSection } from "@/components/local-dismantling-section";
import { RegionalServiceLinks } from "@/components/regional-service-links";
import { directoryRegions } from "@/data/directory-pages";
import {
  normalizeDirectoryText,
  providerCantonIds,
  providerDeclaredServiceArea,
  providerWebsite,
} from "@/lib/directory";
import { directoryPageInfo, type DirectoryPageData } from "@/lib/directory-seo";
import type { ProviderRow } from "@/lib/public-content.types";

export function DirectoryPage(data: DirectoryPageData) {
  const { kind, region, specialty, providers, serviceAreaProviders = [], unavailable } = data;
  const isAgent = kind === "agent";
  const info = directoryPageInfo(data);
  const [query, setQuery] = useState("");
  const [canton, setCanton] = useState("");
  const filtered = useMemo(
    () =>
      providers.filter((provider) => {
        if (canton && !providerCantonIds(provider).includes(canton)) return false;
        return normalizeDirectoryText(
          [
            provider.name,
            provider.location,
            provider.category,
            provider.description,
            ...(provider.services ?? []),
          ].join(" "),
        ).includes(normalizeDirectoryText(query));
      }),
    [providers, query, canton],
  );
  const checks =
    specialty?.checks ??
    (isAgent
      ? [
          "Referenzen für einen ähnlichen Objekttyp und die konkrete Teilregion zeigen lassen.",
          "Bewertungsgrundlage, Vermarktung und persönliche Betreuung vergleichen.",
          "Provision, Zusatzkosten und Laufzeit des Mandats schriftlich vereinbaren.",
        ]
      : [
          "Referenzen für vergleichbare Arbeiten und den Einsatzort prüfen.",
          "Leistung, Material, Anfahrt und Entsorgung mit gleichem Umfang vergleichen.",
          "Termine, Zahlungsplan und Vorgehen bei Zusatzarbeiten schriftlich festhalten.",
        ]);
  const faqs = isAgent
    ? [
        {
          question: region
            ? `Wie finde ich einen passenden Makler im Kanton ${region.name}?`
            : "Wie vergleiche ich Immobilienmakler?",
          answer:
            "Vergleichen Sie veröffentlichte Profile nach Standort, Leistungen und Referenzen. Überregionale Anbieter mit einem im Profil genannten Tätigkeitsgebiet stehen in einem eigenen Abschnitt. Bestätigen Sie dort den Einsatz in Ihrer Gemeinde und die Betreuungssprache. Fragen Sie nach Erfahrung mit Ihrem Objekttyp und nach den Leistungen im Mandat.",
        },
        {
          question: "Was kostet die Beauftragung eines Maklers?",
          answer:
            "Das Honorar vereinbaren Sie mit dem jeweiligen Makler. Lassen Sie Provision oder Pauschale, zusätzliche Auslagen, Mehrwertsteuer und den Zeitpunkt der Zahlung im Angebot aufführen. Begner legt das Vermittlungshonorar des Maklers nicht fest.",
        },
        {
          question: "Sind Hausverkauf und Immobilienbewertung dieselbe Leistung?",
          answer:
            "Eine Bewertung dient der Einordnung des Objektwerts. Ein Verkaufsmandat kann zusätzlich Dossier, Vermarktung, Besichtigungen und Verhandlungen umfassen. Klären Sie, welche Leistungen enthalten sind und ob eine Bewertung auch ohne Verkaufsmandat angeboten wird.",
        },
      ]
    : [
        {
          question: region
            ? `Welche Firmen zeigt das Handwerkerverzeichnis ${region.name}?`
            : `Wie werden Firmen für ${specialty?.name ?? "mein Projekt"} ausgewählt?`,
          answer: region
            ? `Die erste Liste zeigt veröffentlichte Anbieter, deren Standortangaben dem Kanton ${region.name} zugeordnet werden können. Überregionale Anbieter mit einem im Profil genannten Tätigkeitsgebiet stehen gegebenenfalls darunter. Ob ein Betrieb Ihre Gemeinde bedient und Kapazität hat, klären Sie direkt.`
            : "Die Zuordnung erfolgt anhand der Kategorie und Leistungen im veröffentlichten Profil. Prüfen Sie im direkten Gespräch, ob der Betrieb die gewünschte Arbeit tatsächlich ausführt und welche Referenzen er dafür vorweisen kann.",
        },
        {
          question: "Kann ich einen Betrieb direkt kontaktieren?",
          answer:
            "Ja. Sie können die im Profil veröffentlichten Telefon-, E-Mail- und Websiteangaben nutzen. Wenn Sie zunächst mehrere Offerten für ein konkretes Projekt suchen, können Sie alternativ eine Anfrage auf Begner eingeben.",
        },
        {
          question: "Worauf sollte ich beim Vergleich von Offerten achten?",
          answer:
            "Vergleichen Sie Angebote mit gleichem Leistungsumfang. Prüfen Sie Material, Vorarbeiten, Anfahrt, Entsorgung, Mehrwertsteuer und mögliche Zusatzkosten. Lassen Sie offene Punkte vor der Auftragserteilung schriftlich klären.",
        },
      ];

  return (
    <>
      <section className="border-b border-border bg-surface py-10 md:py-16">
        <div className="container-page">
          <nav
            aria-label="Brotkrümelnavigation"
            className="mb-7 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground"
          >
            <Link to="/" className="hover:underline">
              Startseite
            </Link>
            <span aria-hidden="true">/</span>
            {region || specialty ? (
              <>
                <Link to={info.hubPath} className="hover:underline">
                  {info.hubName}
                </Link>
                <span aria-hidden="true">/</span>
                <span aria-current="page">{region?.name ?? specialty?.name}</span>
              </>
            ) : (
              <span aria-current="page">{info.hubName}</span>
            )}
          </nav>
          <p className="text-sm font-semibold text-brand-deep">
            {info.hubName} · {region ? `Kanton ${region.name}` : "Schweiz"}
          </p>
          <h1 className="mt-3 max-w-4xl font-display text-3xl font-bold leading-tight sm:text-4xl md:text-5xl">
            {info.heading}
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-8 text-muted-foreground">
            {specialty?.intro ??
              (region && serviceAreaProviders.length > 0
                ? `Vergleichen Sie veröffentlichte ${isAgent ? "Maklerprofile" : "Firmenprofile"} nach Standort und angegebenem Tätigkeitsgebiet. Überregionale Anbieter sind separat aufgeführt. Bestätigen Sie mit dem Anbieter, ob er Ihr Vorhaben im Kanton ${region.name} betreut.`
                : region
                  ? `Vergleichen Sie veröffentlichte ${isAgent ? "Maklerprofile" : "Firmenprofile"} mit Standortangaben im Kanton ${region.name}. Prüfen Sie Leistungen und Kontaktdaten und besprechen Sie Ihr Vorhaben direkt mit einem passenden Anbieter.`
                  : "Finden Sie Immobilienmakler nach Kanton und Leistung. Vergleichen Sie veröffentlichte Profile für den Verkauf oder die Bewertung Ihrer Immobilie und nehmen Sie direkt Kontakt auf.")}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl">
              <a
                href={
                  providers.length === 0 && serviceAreaProviders.length > 0
                    ? "#ueberregionale-anbieter"
                    : "#anbieter"
                }
              >
                {isAgent ? "Maklerprofile" : "Firmenprofile"} ansehen
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl bg-card">
              {isAgent ? (
                <Link to="/registrieren" search={{ typ: "immobilienmakler" }}>
                  Als Makler eintragen
                </Link>
              ) : (
                <Link to="/auftrag-einstellen">Offerten für ein Projekt anfragen</Link>
              )}
            </Button>
          </div>
        </div>
      </section>

      {region && (
        <section className="py-10 md:py-12">
          <div className="container-page grid gap-7 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <h2 className="font-display text-2xl font-bold">
                {isAgent ? "Maklerwahl" : "Betriebsauswahl"} im Kanton {region.name}
              </h2>
              <p className="mt-4 leading-7 text-muted-foreground">
                {isAgent ? region.agentAdvice : region.contractorAdvice}
              </p>
            </div>
            <aside className="rounded-2xl border border-border bg-surface p-6">
              <h2 className="font-display text-lg font-bold">Den Objektstandort genau angeben</h2>
              <p className="mt-3 text-sm leading-7 text-muted-foreground">
                Zum Kanton gehören unter anderem {region.locations.join(", ")}. Nennen Sie Gemeinde
                und Postleitzahl bei Ihrer Kontaktaufnahme. Die Standortangabe im Profil bestätigt
                keine kantonsweite Verfügbarkeit.
              </p>
            </aside>
          </div>
        </section>
      )}

      {region && !isAgent && <RegionalServiceLinks region={region} />}

      {region && !isAgent && (
        <LocalClearanceSection
          location={region.name}
          cantonName={region.name}
          locations={region.locations}
        />
      )}

      <section id="anbieter" className="scroll-mt-24 py-10 md:py-12">
        <div className="container-page">
          <h2 className="font-display text-2xl font-bold">
            {isAgent ? "Veröffentlichte Maklerprofile" : "Veröffentlichte Firmenprofile"}
            {region ? ` im Kanton ${region.name}` : ""}
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-muted-foreground">
            {specialty
              ? "Die Auswahl basiert auf den veröffentlichten Kategorien und Leistungen. Den genauen Leistungsumfang klären Sie direkt mit dem Anbieter."
              : "Vergleichen Sie die Angaben im Profil. Einsatzgebiet, Termine und Konditionen vereinbaren Sie direkt mit dem jeweiligen Anbieter."}
          </p>
          {!unavailable && providers.length > 0 && (
            <div className="mt-6 grid gap-4 rounded-2xl border border-border bg-surface p-4 sm:grid-cols-2">
              <label className="text-sm font-semibold">
                Firma oder Stichwort
                <span className="relative mt-2 block">
                  <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Name, Ort oder Leistung"
                    className="h-11 w-full min-w-0 rounded-lg border border-input bg-background pl-10 pr-3 font-normal"
                  />
                </span>
              </label>
              {!region && (
                <label className="text-sm font-semibold">
                  Kanton
                  <select
                    value={canton}
                    onChange={(e) => setCanton(e.target.value)}
                    className="mt-2 h-11 w-full rounded-lg border border-input bg-background px-3 font-normal"
                  >
                    <option value="">Alle Kantone</option>
                    {directoryRegions.map((r) => (
                      <option key={r.canton} value={r.canton}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
          <p role="status" className="mt-6 text-sm text-muted-foreground">
            {unavailable
              ? "Die Profile sind momentan nicht abrufbar."
              : `${filtered.length} ${filtered.length === 1 ? "Profil" : "Profile"} gefunden`}
          </p>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            {filtered.map((provider) => (
              <DirectoryProviderCard key={provider.id} provider={provider} />
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="mt-5 rounded-2xl border border-dashed border-border bg-surface p-6 sm:p-8">
              <h3 className="font-display text-xl font-bold">
                {unavailable
                  ? "Bitte versuchen Sie es später erneut"
                  : providers.length
                    ? "Keine Profile für diese Filter"
                    : serviceAreaProviders.length > 0
                      ? `Aktuell keine Profile mit Standort im Kanton ${region?.name}`
                      : "Aktuell keine passenden Profile veröffentlicht"}
              </h3>
              <p className="mt-3 max-w-2xl leading-7 text-muted-foreground">
                {unavailable
                  ? "Die Auswahlhilfen und regionalen Verzeichnisse stehen Ihnen weiterhin zur Verfügung."
                  : providers.length
                    ? "Ändern Sie den Suchbegriff oder wählen Sie einen anderen Kanton."
                    : serviceAreaProviders.length > 0
                      ? "Im folgenden Abschnitt finden Sie überregionale Anbieter mit einem veröffentlichten Tätigkeitsgebiet. Fragen Sie direkt nach der Betreuung Ihres Objektstandorts und der Erfahrung mit Ihrem Vorhaben."
                      : "Im allgemeinen Verzeichnis können Sie weitere Anbieter und deren Leistungsangaben prüfen. Fragen Sie direkt nach dem Einsatzgebiet und der Erfahrung mit Ihrem Vorhaben."}
              </p>
              {providers.length ? (
                <Button
                  variant="outline"
                  className="mt-5"
                  onClick={() => {
                    setQuery("");
                    setCanton("");
                  }}
                >
                  Filter zurücksetzen
                </Button>
              ) : serviceAreaProviders.length > 0 ? (
                <Button asChild variant="outline" className="mt-5">
                  <a href="#ueberregionale-anbieter">Überregionale Anbieter ansehen</a>
                </Button>
              ) : region || specialty ? (
                <Button asChild variant="outline" className="mt-5">
                  <Link to={info.hubPath}>Gesamtes {info.hubName} ansehen</Link>
                </Button>
              ) : (
                <Button asChild variant="outline" className="mt-5">
                  <Link
                    to="/registrieren"
                    search={{ typ: isAgent ? "immobilienmakler" : "auftragnehmer" }}
                  >
                    Unternehmen eintragen
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {region && serviceAreaProviders.length > 0 && (
        <section
          id="ueberregionale-anbieter"
          className="scroll-mt-24 border-y border-border bg-surface py-10 md:py-12"
        >
          <div className="container-page">
            <h2 className="font-display text-2xl font-bold">
              Überregional tätige {isAgent ? "Immobilienmakler" : "Firmen"} für {region.name}
            </h2>
            <p className="mt-3 max-w-3xl leading-7 text-muted-foreground">
              Diese Anbieter nennen die Deutschschweiz als Tätigkeitsgebiet in ihrem
              veröffentlichten Profil. Die Aufnahme in diesen Abschnitt beruht auf dem
              Tätigkeitsgebiet; der Firmensitz ist beim Profil angegeben. Fragen Sie nach der
              Betreuung Ihrer Gemeinde, der gewünschten Sprache und möglichen Anfahrtskosten. In
              mehrsprachigen Kantonen bezieht sich die Gebietsangabe auf deutschsprachige Regionen.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              {serviceAreaProviders.length}{" "}
              {serviceAreaProviders.length === 1
                ? "überregionales Profil"
                : "überregionale Profile"}
            </p>
            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              {serviceAreaProviders.map((provider) => (
                <DirectoryProviderCard key={provider.id} provider={provider} />
              ))}
            </div>
          </div>
        </section>
      )}

      {!isAgent && (region || specialty?.slug === "raeumung") && (
        <LocalDismantlingSection
          location={region?.name ?? "Schweiz"}
          cantonName={region?.name}
          locations={region?.locations}
        />
      )}

      {region && !isAgent && (
        <LocalCleaningSection
          location={region.name}
          cantonName={region.name}
          locations={region.locations}
        />
      )}

      <section className="border-y border-border bg-surface py-12">
        <div className="container-page grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold">
              {specialty
                ? `${specialty.name}: Darauf kommt es bei der Auswahl an`
                : `So vergleichen Sie ${isAgent ? "Immobilienmakler" : "Handwerksbetriebe"}`}
            </h2>
            <ul className="mt-5 space-y-4">
              {checks.map((check) => (
                <li key={check} className="flex gap-3 leading-7">
                  <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
                  {check}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-xl font-bold">
              {isAgent ? "Das Erstgespräch vorbereiten" : "Eine vergleichbare Anfrage vorbereiten"}
            </h2>
            <p className="mt-4 leading-7 text-muted-foreground">
              {isAgent
                ? "Halten Sie Standort, Objekttyp, ungefähre Flächen, Baujahr, bekannte Erneuerungen und Ihren zeitlichen Rahmen bereit. Fragen Sie, welche weiteren Unterlagen für Bewertung und Vermarktung benötigt werden."
                : "Notieren Sie Standort, Masse, gewünschten Umfang und Zeitraum. Fotos vom aktuellen Zustand und Hinweise zu Zugang und Nutzung helfen dem Betrieb, eine Besichtigung oder eine Offerte vorzubereiten."}
            </p>
            <div className="mt-5 flex flex-wrap gap-4">
              {isAgent ? (
                <>
                  <Link to="/immobilien" className="font-semibold text-brand-deep hover:underline">
                    Immobilie selbst inserieren
                  </Link>
                  <Link
                    to="/mehrfamilienhaus-verkaufen"
                    className="font-semibold text-brand-deep hover:underline"
                  >
                    Mehrfamilienhaus diskret verkaufen
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/auftrag-einstellen"
                    className="font-semibold text-brand-deep hover:underline"
                  >
                    Projekt kostenlos eingeben
                  </Link>
                  <Link to="/preise" className="font-semibold text-brand-deep hover:underline">
                    Richtpreise ansehen
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container-page max-w-4xl">
          <h2 className="font-display text-2xl font-bold">Häufige Fragen zum {info.hubName}</h2>
          <div className="mt-5 divide-y divide-border">
            {faqs.map((faq) => (
              <details key={faq.question} className="py-4">
                <summary className="cursor-pointer py-2 font-semibold">{faq.question}</summary>
                <p className="pb-2 pt-3 leading-7 text-muted-foreground">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <DirectoryNavigation kind={kind} />
    </>
  );
}

export function DirectoryProviderCard({ provider }: { provider: ProviderRow }) {
  const website = providerWebsite(provider.website);
  const serviceArea = providerDeclaredServiceArea(provider);
  return (
    <article
      id={`anbieter-${provider.id}`}
      className="scroll-mt-24 rounded-2xl border border-border bg-card p-6 shadow-card"
    >
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-display text-xl font-bold">{provider.name}</h3>
        {provider.verified && (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft px-2 py-1 text-xs font-semibold text-brand-deep">
            <BadgeCheck className="h-3.5 w-3.5" />
            Verifiziert
          </span>
        )}
      </div>
      <p className="mt-2 text-sm font-medium text-brand-deep">{provider.category}</p>
      <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {serviceArea ? `Firmensitz: ${provider.location}` : provider.location}
        {!serviceArea && provider.canton ? ` · ${provider.canton}` : ""}
      </p>
      {serviceArea && (
        <p className="mt-2 text-sm text-muted-foreground">
          Tätigkeitsgebiet laut Profil: {serviceArea}
        </p>
      )}
      {provider.description && (
        <p className="mt-4 whitespace-pre-line break-words text-sm leading-7 text-muted-foreground">
          {provider.description}
        </p>
      )}
      {provider.services?.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-2" aria-label="Veröffentlichte Leistungen">
          {provider.services.map((service, index) => (
            <li key={`${service}-${index}`} className="rounded-full bg-surface px-3 py-1 text-xs">
              {service}
            </li>
          ))}
        </ul>
      )}
      {provider.rating !== null && provider.review_count > 0 && (
        <p className="mt-4 flex items-center gap-2 text-sm">
          <Star className="h-4 w-4 fill-current text-brand" aria-hidden="true" />
          {provider.rating.toLocaleString("de-CH", { maximumFractionDigits: 1 })} / 5 ·{" "}
          {provider.review_count} Bewertungen
        </p>
      )}
      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-3 border-t border-border pt-4 text-sm font-semibold text-brand-deep">
        {provider.phone && (
          <a
            href={`tel:${provider.phone.replace(/[^+0-9]/g, "")}`}
            aria-label={`${provider.name} anrufen`}
            className="inline-flex min-h-10 items-center gap-2 break-all hover:underline"
          >
            <Phone className="h-4 w-4 shrink-0" />
            {provider.phone}
          </a>
        )}
        {provider.email && (
          <a
            href={`mailto:${provider.email}`}
            aria-label={`E-Mail an ${provider.name}`}
            className="inline-flex min-h-10 items-center gap-2 hover:underline"
          >
            <Mail className="h-4 w-4" />
            E-Mail
          </a>
        )}
        {website && (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer ugc"
            aria-label={`Website von ${provider.name} öffnen`}
            className="inline-flex min-h-10 items-center gap-2 hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
            Website
          </a>
        )}
        {!provider.phone && !provider.email && !website && (
          <span className="text-muted-foreground">Keine direkten Kontaktdaten veröffentlicht.</span>
        )}
      </div>
    </article>
  );
}

export function DirectoryNotFound() {
  return (
    <div className="container-page py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Verzeichnis nicht gefunden</h1>
      <p className="mt-4 text-muted-foreground">
        Wählen Sie ein Verzeichnis, um eine Region oder ein Fachgebiet zu finden.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Button asChild>
          <Link to="/auftragnehmer">Handwerkerverzeichnis</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/makler">Maklerverzeichnis</Link>
        </Button>
      </div>
    </div>
  );
}
